// src/contexts/TimeTrackingContext.jsx
// Shared state for clock in/out — single source of truth for all ClockInOutButton instances.

import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';

const TimeTrackingContext = createContext();

export function TimeTrackingProvider({ children }) {
  const { user } = useUser();
  const [visible, setVisible] = useState(false);
  const [mileageOnly, setMileageOnly] = useState(false);
  const [trackMileage, setTrackMileage] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  // Check org settings to determine visibility
  useEffect(() => {
    if (!user?.organization_id || !user?.employment_type) {
      setVisible(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    apiService.getOrganization(user.organization_id)
      .then((org) => {
        if (cancelled) return;
        const tt = org?.settings?.time_tracking;
        const hasMileage = org?.settings?.track_mileage === true;
        const hasTimeTracking = tt?.enabled && tt?.required_employment_types?.includes(user.employment_type);
        setTrackMileage(hasMileage);
        if (hasTimeTracking) {
          setVisible(true);
          setMileageOnly(false);
        } else if (hasMileage) {
          setVisible(true);
          setMileageOnly(true);
        } else {
          setVisible(false);
          setMileageOnly(false);
        }
      })
      .catch(() => {
        if (!cancelled) { setVisible(false); setMileageOnly(false); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.organization_id, user?.employment_type]);

  // Fetch current active entry when visible (skip for mileage-only users)
  const fetchCurrent = useCallback(async () => {
    if (!visible || mileageOnly) return;
    try {
      const entry = await apiService.getCurrentTimeEntry();
      setActiveEntry(Array.isArray(entry) ? null : entry || null);
    } catch {
      setActiveEntry(null);
    }
  }, [visible, mileageOnly]);

  useEffect(() => {
    if (visible && !mileageOnly) fetchCurrent();
  }, [visible, mileageOnly, fetchCurrent]);

  // Timer interval when clocked in
  useEffect(() => {
    if (activeEntry?.clock_in) {
      const updateElapsed = () => setElapsed(Date.now() - activeEntry.clock_in);
      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);
      return () => clearInterval(timerRef.current);
    } else {
      setElapsed(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [activeEntry]);

  const clockIn = useCallback(async (locationId) => {
    const entry = await apiService.clockIn(locationId);
    setActiveEntry(entry);
    return entry;
  }, []);

  const clockOut = useCallback(async (notes, miles, hourAllocations) => {
    const entry = await apiService.clockOut(notes || undefined, miles || undefined, hourAllocations || undefined);
    setActiveEntry(null);
    return entry;
  }, []);

  return (
    <TimeTrackingContext.Provider value={{ visible, mileageOnly, activeEntry, elapsed, loading, trackMileage, clockIn, clockOut }}>
      {children}
    </TimeTrackingContext.Provider>
  );
}

export function useTimeTracking() {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  }
  return context;
}
